import { JellyfinItem } from '../api/jellyfin';
import { isPublicProduct } from '../productProfile';

export type PlaybackSource = NonNullable<JellyfinItem['MediaSources']>[number];

const smallCaps: Record<string, string> = {
    'ᴀ': 'a', 'ʙ': 'b', 'ᴄ': 'c', 'ᴅ': 'd', 'ᴇ': 'e', 'ꜰ': 'f', 'ɢ': 'g', 'ʜ': 'h',
    'ɪ': 'i', 'ᴊ': 'j', 'ᴋ': 'k', 'ʟ': 'l', 'ᴍ': 'm', 'ɴ': 'n', 'ᴏ': 'o', 'ᴘ': 'p',
    'ʀ': 'r', 'ꜱ': 's', 'ᴛ': 't', 'ᴜ': 'u', 'ᴠ': 'v', 'ᴡ': 'w', 'ʏ': 'y', 'ᴢ': 'z',
};

function normalize(value?: string) {
    return (value ?? '')
        .replace(/[\u200B-\u200D\u2060\uFEFF]/g, '')
        .replace(/[\u1d00\u0299\u1d04\u1d05\u1d07\ua730\u0262\u029c\u026a\u1d0a\u1d0b\u029f\u1d0d\u0274\u1d0f\u1d18\u0280\ua731\u1d1b\u1d1c\u1d20\u1d21\u028f\u1d22]/g, (letter) => smallCaps[letter] ?? letter)
        .replace(/ᴹᵇᵖˢ/gi, 'mbps')
        .replace(/[\n\r]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
}

function languageTags(text: string, source: PlaybackSource) {
    const aliases: Record<string, string> = {
        en: 'EN', eng: 'EN', english: 'EN', ko: 'KO', kor: 'KO', korean: 'KO',
        ja: 'JA', jpn: 'JA', japanese: 'JA', es: 'ES', spa: 'ES', spanish: 'ES',
        fr: 'FR', fra: 'FR', french: 'FR', de: 'DE', ger: 'DE', german: 'DE',
        it: 'IT', ita: 'IT', italian: 'IT', ru: 'RU', rus: 'RU', russian: 'RU',
        zh: 'ZH', chi: 'ZH', chinese: 'ZH', pt: 'PT', por: 'PT', portuguese: 'PT',
    };
    const found: string[] = [];
    const add = (value?: string) => {
        const tag = value ? aliases[value.toLowerCase()] : undefined;
        if (tag && !found.includes(tag)) found.push(tag);
    };
    text.match(/\b(en|eng|english|ko|kor|korean|ja|jpn|japanese|es|spa|spanish|fr|fra|french|de|ger|german|it|ita|italian|ru|rus|russian|zh|chi|chinese|pt|por|portuguese)\b/g)?.forEach(add);
    source.MediaStreams?.filter((stream) => stream.Type === 'Audio').forEach((stream) => add(stream.Language));
    return found.slice(0, 2);
}

const providerSuffixes: Record<string, string> = {
    tb: 'TB',
    rd: 'RD',
    ad: 'AD',
    pm: 'PM',
    dl: 'DL',
    ed: 'ED',
    oc: 'OC',
    db: 'DB',
};

/** Preserve the bare provider suffix emitted by Remux/ObnoxiousTV version names. */
export function getPlaybackSourceProviderLabel(name?: string) {
    const raw = name ?? '';
    if (/\btorbox\b|\[tb\]/i.test(raw)) return 'TB';
    if (/\breal[ -]?debrid\b|\[rd\]/i.test(raw)) return 'RD';
    if (/\ball[ -]?debrid\b|\[ad\]/i.test(raw)) return 'AD';
    if (/\bpremiumize\b|\[pm\]/i.test(raw)) return 'PM';
    if (/\bdebrid[ -]?link\b|\[dl\]/i.test(raw)) return 'DL';
    if (/\beasy[ -]?debrid\b|\[ed\]/i.test(raw)) return 'ED';
    if (/\boffcloud\b|\[oc\]/i.test(raw)) return 'OC';
    if (/\bdeepbrid\b|\[db\]/i.test(raw)) return 'DB';
    const suffix = normalize(raw).match(/(?:^|[\s|·])(tb|rd|ad|pm|dl|ed|oc|db)$/i)?.[1];
    return suffix ? providerSuffixes[suffix.toLowerCase()] ?? '' : '';
}

export function getPlaybackSourceFacts(source: PlaybackSource) {
    const text = normalize(source.Name);
    const video = source.MediaStreams?.find((stream) => stream.Type === 'Video');
    const audio = source.MediaStreams?.find((stream) => stream.Type === 'Audio');
    const height = video?.Height ?? (/\b2160p?\b|\b4k\b/.test(text) ? 2160 : /\b1080p?\b/.test(text) ? 1080 : /\b720p?\b/.test(text) ? 720 : Number(text.match(/\b(\d{3,4})p\b/)?.[1] ?? 0));
    const quality = height >= 2160 ? '4K' : height >= 1080 ? 'FHD' : height >= 720 ? 'HD' : height ? `${height}p` : '';
    // Probe data (cached ffprobe fields) is measured and beats release-name
    // tags; filename text is only a fallback for never-probed sources.
    const probeCodecText = (video?.Codec ?? source.VideoCodec ?? '').toLowerCase();
    const codecFromProbe = /\b(hevc|h\.?265|x265)\b/.test(probeCodecText) ? 'HEVC' : /\b(avc|h\.?264|x264)\b/.test(probeCodecText) ? 'H264' : /\bav1\b/.test(probeCodecText) ? 'AV1' : '';
    const codecFromName = /\b(hevc|h\.?265|x265)\b/.test(text) ? 'HEVC' : /\b(avc|h\.?264|x264)\b/.test(text) ? 'H264' : /\bav1\b/.test(text) ? 'AV1' : '';
    const codec = codecFromProbe || codecFromName || (video?.Codec ?? source.VideoCodec ?? 'H264').toUpperCase();
    const bitrateValue = text.match(/\b(\d+(?:\.\d+)?)\s*mbps\b/)?.[1];
    // source.Bitrate only exists when the server holds cached probe data.
    const bitrateMbps = source.Bitrate ? source.Bitrate / 1000000 : bitrateValue ? Number(bitrateValue) : 0;
    const bitrate = source.Bitrate ? `${Math.round(source.Bitrate / 100000) / 10} Mbps` : bitrateValue ? `${Number(bitrateValue)} Mbps` : '';
    const sizeMatch = text.match(/\b(\d+(?:\.\d+)?)\s*(tb|gb|mb)(?:\s*\/[^\s]+)?\b/);
    const size = sizeMatch ? `${Number(sizeMatch[1])} ${sizeMatch[2].toUpperCase()}` : source.Size ? `${Number((source.Size / 1073741824).toFixed(2))} GB` : '';
    const genericRemuxBucket = /^(?:uhd|fhd|hd|sd)\s+remux(?:\s*[|·]|\s*$)/i.test(source.Name?.trim() ?? '');
    const releaseType = !genericRemuxBucket && /\b(remux|bdremux)\b/.test(text) ? 'Remux' : /\b(blu-?ray|bluray|bdrip|brrip)\b/.test(text) ? 'Bluray' : /\bweb-?dl\b/.test(text) ? 'WEB-DL' : /\bwebrip\b/.test(text) ? 'WEBRip' : /\bhdtv\b/.test(text) ? 'HDTV' : '';
    const visual = /\b(dolby vision|dovi|dv)\b/.test(text) ? 'DV' : /\bhdr10\+\b/.test(text) ? 'HDR10+' : /\bhdr10\b/.test(text) ? 'HDR10' : /\bhdr\b/.test(text) ? 'HDR' : '';
    const audioText = `${text} ${audio?.Codec ?? ''}`;
    // Probe codec/channels are measured and beat release-name audio tags.
    const probeAudioText = (audio?.Codec ?? '').toLowerCase();
    const audioCodecFromProbe = /\btruehd\b/.test(probeAudioText) ? 'TrueHD' : /\bdts[- ]?hd\b/.test(probeAudioText) ? 'DTS-HD' : /\bdts\b/.test(probeAudioText) ? 'DTS' : /\b(eac3|e-ac-3|dd\+)\b/.test(probeAudioText) ? 'DD+' : /\b(ac3|ac-3)\b/.test(probeAudioText) ? 'DD' : /\baac\b/.test(probeAudioText) ? 'AAC' : /\bflac\b/.test(probeAudioText) ? 'FLAC' : /\bopus\b/.test(probeAudioText) ? 'Opus' : /\bmp3\b/.test(probeAudioText) ? 'MP3' : '';
    const audioCodecFromName = /\btruehd\b/.test(audioText) ? 'TrueHD' : /\bdts[- ]?hd\b/.test(audioText) ? 'DTS-HD' : /\bdts\b/.test(audioText) ? 'DTS' : /\b(eac3|e-ac-3|dd\+)\b/.test(audioText) ? 'DD+' : /\b(ac3|ac-3)\b/.test(audioText) ? 'DD' : /\baac\b/.test(audioText) ? 'AAC' : /\bflac\b/.test(audioText) ? 'FLAC' : /\bopus\b/.test(audioText) ? 'Opus' : /\bmp3\b/.test(audioText) ? 'MP3' : '';
    const audioCodecBase = audioCodecFromProbe || audioCodecFromName;
    const audioCodec = (audioCodecBase === 'TrueHD' || audioCodecBase === 'DD+') && /\batmos\b/.test(audioText)
        ? `${audioCodecBase} Atmos`
        : audioCodecBase || (audio?.Codec ?? '').toUpperCase();
    const layout = audio?.Channels === 8 ? '7.1' : audio?.Channels === 6 ? '5.1' : audio?.Channels === 2 ? '2.0' : audio?.Channels === 1 ? '1.0' : /\b7\.1\b/.test(text) ? '7.1' : /\b5\.1\b/.test(text) ? '5.1' : /\b2\.0\b/.test(text) ? '2.0' : '';
    const provider = getPlaybackSourceProviderLabel(source.Name);
    const origin = source.Name?.match(/(?:^|\n)\s*⛉\s*([^\n]+)/u)?.[1]?.replace(/^\[(?:TB|RD|AD|PM|DL|ED)\]\s*/i, '').replace(/\s*[»⛿].*$/u, '').trim() ?? '';
    return { quality, releaseType, codec, visual, bitrate, bitrateMbps, size, languages: languageTags(text, source), audioCodec, layout, container: (source.Container ?? 'MP4').toUpperCase(), origin, provider, text };
}

export function orderPlaybackSources(sources: PlaybackSource[]) {
    return sources
        .filter((source) => Boolean(source.Id))
        .map((source, index) => ({ source, index, facts: getPlaybackSourceFacts(source) }))
        .sort((left, right) => {
            const leftEn = left.facts.text.search(/\ben\b/);
            const leftKo = left.facts.text.search(/\bko\b/);
            const leftSub = left.facts.text.search(/\bsub\b/);
            const rightEn = right.facts.text.search(/\ben\b/);
            const rightKo = right.facts.text.search(/\bko\b/);
            const rightSub = right.facts.text.search(/\bsub\b/);
            const leftEnglishKoreanAudio = Number(leftEn >= 0 && leftKo > leftEn && (leftSub < 0 || leftSub > leftKo));
            const rightEnglishKoreanAudio = Number(rightEn >= 0 && rightKo > rightEn && (rightSub < 0 || rightSub > rightKo));
            if (rightEnglishKoreanAudio !== leftEnglishKoreanAudio) return rightEnglishKoreanAudio - leftEnglishKoreanAudio;
            if (leftEnglishKoreanAudio && rightEnglishKoreanAudio && left.facts.bitrateMbps !== right.facts.bitrateMbps) {
                if (!left.facts.bitrateMbps) return 1;
                if (!right.facts.bitrateMbps) return -1;
                return left.facts.bitrateMbps - right.facts.bitrateMbps;
            }
            const leftBilingual = Number(left.facts.languages.includes('EN') && left.facts.languages.includes('KO'));
            const rightBilingual = Number(right.facts.languages.includes('EN') && right.facts.languages.includes('KO'));
            if (rightBilingual !== leftBilingual) return rightBilingual - leftBilingual;
            const leftEnglish = Number(left.facts.languages.includes('EN'));
            const rightEnglish = Number(right.facts.languages.includes('EN'));
            if (rightEnglish !== leftEnglish) return rightEnglish - leftEnglish;
            return left.index - right.index;
        })
        .map((entry) => entry.source);
}

export function getPlaybackSourceLabel(source?: PlaybackSource, index = 0) {
    if (!source) return `Version ${index + 1}`;
    if (isPublicProduct) return source.Name?.trim() || `Version ${index + 1}`;
    const facts = getPlaybackSourceFacts(source);
    const primary = [facts.quality, facts.releaseType, facts.codec, facts.visual, facts.bitrate, facts.size].filter(Boolean).filter((value, valueIndex, values) => values.findIndex((candidate) => candidate.toLowerCase() === value.toLowerCase()) === valueIndex).join(' ');
    const languages = facts.languages.join('/');
    const audio = [facts.audioCodec, facts.layout].filter(Boolean).join(' ');
    return [primary, languages, audio, facts.container, facts.origin, facts.provider].filter(Boolean).filter((value, valueIndex, values) => values.findIndex((candidate) => candidate.toLowerCase() === value.toLowerCase()) === valueIndex).join(' | ') || `Version ${index + 1}`;
}
